import React, { useState } from 'react';
import { MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';

/**
 * Manual -> Text: type a number, type a message, send.
 *
 * The full two-way inbox (SmsInbox) used to sit here, which made "text one
 * tenant quickly" a tour through conversation threads. The operator asked for
 * the split: Manual is for COMPOSING, and the conversation history lives on
 * its own Text log tab. Posts to the same /sms/send endpoint the inbox uses,
 * so replies still land in the Text log thread for that number.
 */
const TextCompose = ({ facility }) => {
  const facilityId = facility?._id || facility?.id || '';
  const [toPhone, setToPhone] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const phone = toPhone.trim();
    if (!phone) { toast.error('Enter a phone number.'); return; }
    if (!body.trim()) { toast.error('Type a message.'); return; }
    setSending(true);
    try {
      await apiClient.post(`/api/facilities/${facilityId}/sms/send`, {
        to_phone: phone,
        body: body.trim(),
      });
      toast.success(`Text sent to ${phone}.`);
      setBody('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  const chars = body.length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 max-w-xl space-y-4">
      <div className="flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-blue-600" />
        <h2 className="text-[15px] font-semibold text-slate-900">Send a text</h2>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-slate-600 mb-1">
          To (phone number)
        </label>
        <input
          type="tel"
          placeholder="e.g. (903) 555-0142"
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px]"
          value={toPhone}
          onChange={(e) => setToPhone(e.target.value)}
        />
      </div>

      <div>
        <label className="block text-[12px] font-medium text-slate-600 mb-1">
          Message
        </label>
        <textarea
          rows={4}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px]"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <p className="text-[11px] text-slate-400 mt-1">
          {chars} characters
          {chars > 160 && ' — over 160, sends as multiple segments'}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate-400">
          Replies appear in the Text log.
        </p>
        <button
          type="button"
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" /> {sending ? 'Sending…' : 'Send text'}
        </button>
      </div>
    </div>
  );
};

export default TextCompose;
